import React from "react";
import {Box, Fab, IconButton, TextField} from "@mui/material";
import {PD_SM, SZ_CHAT_BAR} from "shared/dimens";
import {Attachment, EmojiEmotionsOutlined, MicOutlined, SendOutlined} from "@mui/icons-material";
import {EmojiPopup} from "../EmojiPopup";
import {ICON_BUTTON_XSM_STYLE, TYPING_MAX_DELAY} from "shared/constants";
import {Action} from "shared/types";
import {ActionsDialogContent} from "shared/Dialogs";
import {App} from "app/App";
import {BaseApp} from "../../../../shared/BaseApp";
import {RecordAudioFragment} from "../../../../shared/RecordAudioFragment";

export interface ChatBarListener {
  onSend(text: string): void,

  onTyping(): void,

  onSendMeetingInvite(channel: string): void,
}

export type ChatBarConfig = {
  showEmojiButton?: boolean,
  showAttachButton?: boolean,
  showVoiceInputButton?: boolean,
  onVoiceInputToText?: (url: string) => Promise<string>,
  voiceAutoSend?: boolean,
}

export type ChatBarProps = {
  config?: ChatBarConfig,
  listener: ChatBarListener,
}

type ChatBarState = {
  text: string,
  autoSend?: boolean,
  showFab: boolean,
  emojiPopupAnchorEl?: any,
  stickerPopupAnchorEl?: any,
}

export class ChatBar extends React.PureComponent<ChatBarProps, ChatBarState> {

  private readonly theme = BaseApp.CONTEXT.getAppConfig().theme;

  private lastTypingEventTime: number = 0;
  private typingTimeoutId: any;

  constructor(props) {
    super(props);
    this.state = {
      text: "",
      showFab: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<ChatBarProps>, prevState: Readonly<ChatBarState>, snapshot?: any) {
    if (this.state.autoSend) {
      this.onSend();
      this.setState({
        autoSend: false,
      });
    }
  }

  render() {
    const showFab = this.state.showFab;
    const showEmojiButton = this.props.config?.showEmojiButton;
    const showAttachButton = this.props.config?.showAttachButton;
    const showVoiceInputButton = this.props.config?.showVoiceInputButton;
    return (
      <>
        <Box style={{height: SZ_CHAT_BAR, position: "relative"}}>
          <Box display="flex" flexDirection="row"
               style={{background: this.theme.palette.background.paper, alignItems: "center"}}>
            {showEmojiButton
              ? <>
                <IconButton onClick={(event) => this.onEmojiButtonClicked(event)}>
                  <EmojiEmotionsOutlined style={ICON_BUTTON_XSM_STYLE}/>
                </IconButton>
                <EmojiPopup anchorEl={this.state.emojiPopupAnchorEl} onClose={() => this.onPopupClosed()}/>
              </>
              : null}
            {/*<IconButton onClick={(event) => this.onStickerButtonClicked(event)}>*/}
            {/*  <img src={iconInsertSticker} style={ICON_BUTTON_SM_STYLE_PD}/>*/}
            {/*</IconButton>*/}
            {/*<StickerPopup anchorEl={this.state.stickerPopupAnchorEl} onClose={() => this.onPopupClosed()}/>*/}
            {/*<IconButton>*/}
            {/*  <Mic style={ICON_BUTTON_SM_STYLE_PD}/>*/}
            {/*</IconButton>*/}
            {/*<IconButton onClick={(event) => this.onSendCallInviteButtonClicked(event)}>*/}
            {/*  <VoiceChatOutlined style={ICON_BUTTON_SM_STYLE_PD}/>*/}
            {/*</IconButton>*/}
            <TextField
              InputProps={{disableUnderline: true}}
              autoFocus
              style={{
                flexGrow: 1,
                height: SZ_CHAT_BAR,
                paddingLeft: PD_SM,
                paddingRight: PD_SM,
                minHeight: SZ_CHAT_BAR,
                display: "flex",
                justifyContent: "center"
              }}
              variant='standard'
              value={this.state.text}
              placeholder="Type a message"
              onKeyDown={event => {
                if (event.keyCode === 13) {
                  event.preventDefault();
                  this.onSend();
                }
              }}
              onChange={(event) => {
                let text = event.target.value;
                let now = Date.now();
                let delay = Math.max(0, this.lastTypingEventTime <= 0 ? 0 : (this.lastTypingEventTime + TYPING_MAX_DELAY - now));
                let lastTypingEventTime = now + delay;
                if (this.typingTimeoutId) {
                  clearTimeout(this.typingTimeoutId);
                  this.typingTimeoutId = null;
                }
                this.typingTimeoutId = setTimeout(() => {
                  this.props.listener.onTyping();
                  this.lastTypingEventTime = lastTypingEventTime;
                }, delay);
                this.setState({showFab: text.length > 0, text: text});
              }}
            />
          </Box>
          <Box display='flex' alignItems='center' style={{
            position: 'absolute',
            right: PD_SM,
            bottom: PD_SM,
            gap: PD_SM,
          }}>
            {showAttachButton
              ? <Fab color='default' size='small'>
                <Attachment/>
              </Fab>
              : null}
            {showVoiceInputButton
              ? <Fab
                onClick={() => {
                  const id = BaseApp.CONTEXT.showDialog(null, () => <RecordAudioFragment
                    onDone={url => {
                      BaseApp.CONTEXT.hideDialog(id);
                      const transform = this.props.config?.onVoiceInputToText;
                      const autoSend = Boolean(this.props.config?.voiceAutoSend);
                      if (transform) {
                        const self = this;
                        setTimeout(() => {
                          BaseApp.CONTEXT.showProgressDialog("Please wait...");
                          (async function () {
                            const text = await transform?.(url);
                            BaseApp.CONTEXT.hideProgressDialog();
                            if (text) {
                              self.setState({
                                text,
                                autoSend,
                                showFab: true,
                              });
                            }
                          })();
                        }, 1);
                      }
                    }}/>);
                }}
                color='default' size='small'>
                <MicOutlined/>
              </Fab>
              : null}
            <Box visibility={showFab ? 'visible' : 'hidden'} width={showFab ? 'inherit' : '0px'}>
              <Fab onClick={() => this.onSend()} color="secondary">
                <SendOutlined/>
              </Fab>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  private onSend() {
    const text = this.state.text?.trim();
    if (text?.length > 0) {
      this.props.listener.onSend(text);
    }
    this.setState({
      showFab: false,
      text: ""
    });
  }

  private onPopupClosed() {
    this.setState({
      emojiPopupAnchorEl: null,
      stickerPopupAnchorEl: null,
    });
  }

  private onEmojiButtonClicked(event) {
    this.setState({
      emojiPopupAnchorEl: event.target,
    });
  }

  private onStickerButtonClicked(event) {
    this.setState({
      stickerPopupAnchorEl: event.target,
    });
  }

  private onSendCallInviteButtonClicked(event) {
    let actions: Action[] = [
      // new Action("Audio", () => this.onSendCallInvite(ChatPhonecall.CHANNEL_AUDIO)),
      // new Action("Video", () => this.onSendCallInvite(ChatPhonecall.CHANNEL_VIDEO)),
    ];
    App.CONTEXT.showDialog(null, () => <ActionsDialogContent title="Send call invite" actions={actions}/>);
  }

  private onSendCallInvite(channel: string) {
    this.props.listener.onSendMeetingInvite(channel);
  }
}
